import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MediaCard from "@/components/feature/MediaCard";
import Pagination from "@/components/feature/Pagination";
import FilterBar from "./components/FilterBar";
import {
  applyFilters,
  filterByCategory,
  getCategoryMeta,
  PAGE_SIZE,
  sortMedia,
  sortOptions,
  type FilterState,
  type SortKey,
} from "@/lib/media";

const container = "mx-auto w-full max-w-[1280px] px-4 md:px-6";

const defaultFilters: FilterState = { region: "全部", year: "全部", minRating: 0 };

export default function Category() {
  const { categoryName = "movie" } = useParams();
  const meta = getCategoryMeta(categoryName);

  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [sortKey, setSortKey] = useState<SortKey>("recommend");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setFilters(defaultFilters);
    setSortKey("recommend");
    setPage(1);
  }, [categoryName]);

  useEffect(() => {
    setPage(1);
  }, [filters, sortKey]);

  useEffect(() => {
    document.title = meta
      ? `${meta.name} - 红狐影视最新影视资源推荐`
      : "未找到分类 - 红狐影视";
  }, [meta]);

  const baseList = useMemo(() => filterByCategory(categoryName), [categoryName]);
  const filtered = useMemo(() => {
    const res = applyFilters(baseList, filters);
    return sortMedia(res, sortKey);
  }, [baseList, filters, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!meta && baseList.length === 0) {
    return (
      <div className={container}>
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">
            没有找到该分类
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            你访问的分类可能不存在，返回首页看看其他推荐吧。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-home-5-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const title = meta?.name ?? "影视列表";
  const description =
    meta?.description ?? "浏览精彩影视作品，发现值得观看的电影与剧集。";

  return (
    <div className={`${container} py-6 md:py-8`}>
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "分类", to: "/category/movie" },
          { label: title },
        ]}
      />

      <header className="mt-5 rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
        <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
          {title}
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
          {description}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
          <span>
            共 <strong className="text-primary-600">{filtered.length}</strong> 部作品
          </span>
          <span className="text-background-300">|</span>
          <span>
            每页 {PAGE_SIZE} 条 · 第 {page} / {totalPages} 页
          </span>
        </div>
      </header>

      <div className="mt-5">
        <FilterBar
          filters={filters}
          onChange={setFilters}
          onReset={() => setFilters(defaultFilters)}
        />
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200/70 bg-background-50 px-4 py-3">
        <span className="text-sm text-foreground-500">
          排序方式：{sortOptions.find((s) => s.key === sortKey)?.label}
        </span>
        <div className="flex flex-wrap gap-2">
          {sortOptions.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setSortKey(opt.key)}
              className={`whitespace-nowrap rounded-md px-3.5 py-1.5 text-sm transition-colors cursor-pointer ${
                sortKey === opt.key
                  ? "bg-secondary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-secondary-100"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {pageItems.map((item, i) => (
              <MediaCard key={item.id} item={item} index={i} />
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
          <i className="ri-search-eye-line text-5xl text-foreground-300"></i>
          <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
            未找到相关影视作品
          </h2>
          <p className="mt-2 text-sm text-foreground-500">
            当前筛选条件下没有匹配的作品，试试放宽条件。
          </p>
          <button
            type="button"
            onClick={() => setFilters(defaultFilters)}
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            重置筛选条件
          </button>
        </div>
      )}
    </div>
  );
}